import React, { useEffect, useState, useCallback } from "react";
import { api, errorMessage, API_BASE } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Download, Eye, EyeOff, X, Upload, Award } from "lucide-react";

const TYPES = ["Cash Sponsor", "In-Kind Sponsor", "Cloud Credits", "Tools & Software", "Hiring Partner"];
const TIERS = ["Gold", "Silver", "Bronze", "Community"];
const TIER_BADGE = { Gold: "text-[#FBBF24] border-[#FBBF24]/40", Silver: "text-gray-300 border-gray-400/40", Bronze: "text-[#CD7F32] border-[#CD7F32]/40", Community: "text-brand border-brand/40" };

const empty = { company_name: "", website_url: "", sponsorship_type: "Cash Sponsor", tier: "Gold", contribution_amount: "", notes: "", visible: true, show_on_certificates: true };

export default function SponsorsPanel() {
  const [sponsors, setSponsors] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [form, setForm] = useState(empty);
  const [logoFile, setLogoFile] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/sponsors"); setSponsors(data.sponsors);
      const inq = await api.get("/admin/sponsor-inquiries"); setInquiries(inq.data.inquiries);
    } catch (e) { toast.error(errorMessage(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const resetForm = () => { setForm(empty); setLogoFile(null); setEditingId(null); };

  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({ company_name: s.company_name || "", website_url: s.website_url || "", sponsorship_type: s.sponsorship_type, tier: s.tier, contribution_amount: s.contribution_amount ?? "", notes: s.notes || "", visible: !!s.visible, show_on_certificates: !!s.show_on_certificates });
    setLogoFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const buildFD = (includeLogo) => {
    const fd = new FormData();
    fd.append("company_name", form.company_name.trim());
    fd.append("sponsorship_type", form.sponsorship_type);
    fd.append("tier", form.tier);
    fd.append("website_url", form.website_url.trim());
    fd.append("contribution_amount", form.contribution_amount === "" ? "" : String(form.contribution_amount));
    fd.append("notes", form.notes.trim());
    fd.append("visible", form.visible ? "true" : "false");
    fd.append("show_on_certificates", form.show_on_certificates ? "true" : "false");
    if (includeLogo && logoFile) fd.append("logo", logoFile);
    return fd;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) return toast.error("Company name is required");
    if (!editingId && !logoFile) return toast.error("Logo is required");
    if (logoFile && logoFile.size > 2 * 1024 * 1024) return toast.error("Logo must be under 2 MB");
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/admin/sponsors/${editingId}`, buildFD(true));
        toast.success("Sponsor updated");
      } else {
        await api.post("/admin/sponsors", buildFD(true));
        toast.success("Sponsor added");
      }
      resetForm();
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete sponsor "${s.company_name}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/sponsors/${s.id}`); toast.success("Sponsor deleted"); if (editingId === s.id) resetForm(); await load(); }
    catch (err) { toast.error(errorMessage(err)); }
  };

  const toggleVisible = async (s) => {
    const fd = new FormData();
    fd.append("company_name", s.company_name);
    fd.append("sponsorship_type", s.sponsorship_type);
    fd.append("tier", s.tier);
    fd.append("website_url", s.website_url || "");
    fd.append("contribution_amount", s.contribution_amount ?? "");
    fd.append("notes", s.notes || "");
    fd.append("visible", s.visible ? "false" : "true");
    fd.append("show_on_certificates", s.show_on_certificates ? "true" : "false");
    try { await api.put(`/admin/sponsors/${s.id}`, fd); await load(); }
    catch (err) { toast.error(errorMessage(err)); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/sponsors/export-csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "ideathon_sponsors.csv"; a.click(); URL.revokeObjectURL(url);
    } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div className="space-y-6" data-testid="sponsors-panel">
      {/* Add / Edit form */}
      <form onSubmit={submit} className="card-surface space-y-4" data-testid="sponsor-form">
        <div className="flex items-center justify-between">
          <div className="font-display text-xl text-white flex items-center gap-2"><Award className="w-5 h-5 text-brand" strokeWidth={1.5} /> {editingId ? "Edit Sponsor" : "Add Sponsor"}</div>
          {editingId && <button type="button" onClick={resetForm} className="text-xs label-mono text-gray-400 hover:text-white flex items-center gap-1" data-testid="cancel-edit"><X className="w-3.5 h-3.5" /> Cancel edit</button>}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label-mono block mb-2">Company Name <span className="text-brand">*</span></label>
            <input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className="input-field" placeholder="Acme Cloud" required data-testid="sponsor-name" />
          </div>
          <div>
            <label className="label-mono block mb-2">Website URL <span className="text-gray-500">(optional)</span></label>
            <input value={form.website_url} onChange={(e) => set("website_url", e.target.value)} className="input-field" placeholder="https://acme.com" data-testid="sponsor-website" />
          </div>
          <div>
            <label className="label-mono block mb-2">Sponsorship Type <span className="text-brand">*</span></label>
            <select value={form.sponsorship_type} onChange={(e) => set("sponsorship_type", e.target.value)} className="input-field" data-testid="sponsor-type">
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="label-mono block mb-2">Sponsorship Tier <span className="text-brand">*</span></label>
            <select value={form.tier} onChange={(e) => set("tier", e.target.value)} className="input-field" data-testid="sponsor-tier">
              {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="label-mono block mb-2">Contribution Amount <span className="text-gray-500">(internal only)</span></label>
            <input type="number" value={form.contribution_amount} onChange={(e) => set("contribution_amount", e.target.value)} className="input-field" placeholder="50000" data-testid="sponsor-amount" />
          </div>
          <div>
            <label className="label-mono block mb-2">Logo {editingId ? <span className="text-gray-500">(leave empty to keep)</span> : <span className="text-brand">*</span>}</label>
            <label className="input-field flex items-center gap-2 cursor-pointer text-gray-400 hover:text-white" data-testid="sponsor-logo-label">
              <Upload className="w-4 h-4" /> {logoFile ? logoFile.name : "PNG/JPG · max 2MB"}
              <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} data-testid="sponsor-logo-input" />
            </label>
          </div>
        </div>
        <div>
          <label className="label-mono block mb-2">Notes <span className="text-gray-500">(internal only)</span></label>
          <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="input-field" placeholder="Internal reference notes…" data-testid="sponsor-notes" />
        </div>
        <div className="flex flex-wrap gap-6">
          <Toggle label="Visible on Platform" on={form.visible} onClick={() => set("visible", !form.visible)} testid="sponsor-visible" />
          <Toggle label="Show on Certificates" on={form.show_on_certificates} onClick={() => set("show_on_certificates", !form.show_on_certificates)} testid="sponsor-certs" />
        </div>
        <button type="submit" disabled={busy} className="btn-primary flex items-center gap-2" data-testid="sponsor-submit">
          {busy ? <span className="spinner" /> : <Plus className="w-4 h-4" />} {editingId ? "Save Changes" : "Add Sponsor"}
        </button>
      </form>

      {/* List */}
      <div className="card-surface" data-testid="sponsors-list">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-xl text-white">Sponsors <span className="text-gray-500 text-base">({sponsors.length})</span></div>
          {sponsors.length > 0 && <button onClick={exportCsv} className="btn-secondary !py-2 !px-3 text-xs flex items-center gap-2" data-testid="sponsors-export-csv"><Download className="w-3.5 h-3.5" /> Export CSV</button>}
        </div>
        {sponsors.length === 0 ? (
          <p className="text-gray-500 text-sm py-6 text-center">No sponsors yet. Add your first sponsor above.</p>
        ) : (
          <div className="space-y-3">
            {sponsors.map((s) => (
              <div key={s.id} className="flex items-center gap-4 border border-white/10 p-3 flex-wrap" data-testid={`sponsor-row-${s.id}`}>
                <div className="w-20 h-12 bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                  <img src={`${API_BASE}/sponsors/${s.id}/logo`} alt={s.company_name} className="max-w-full max-h-full object-contain" />
                </div>
                <div className="flex-1 min-w-[160px]">
                  <div className="text-white font-medium">{s.company_name}</div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${TIER_BADGE[s.tier] || ""}`}>{s.tier}</span>
                    <span className="text-xs text-gray-400">{s.sponsorship_type}</span>
                    {s.show_on_certificates && <span className="text-[10px] text-gray-500">· on certs</span>}
                  </div>
                </div>
                <button onClick={() => toggleVisible(s)} title={s.visible ? "Visible — click to hide" : "Hidden — click to show"} className={`p-2 ${s.visible ? "text-[#10B981]" : "text-gray-600"} hover:text-white`} data-testid={`sponsor-toggle-${s.id}`}>
                  {s.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <button onClick={() => startEdit(s)} className="p-2 text-gray-400 hover:text-brand" data-testid={`sponsor-edit-${s.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(s)} className="p-2 text-gray-400 hover:text-[#EF4444]" data-testid={`sponsor-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sponsorship inquiries (lead form submissions) */}
      {inquiries.length > 0 && (
        <div className="card-surface" data-testid="sponsor-inquiries">
          <div className="font-display text-xl text-white mb-4">Sponsorship Inquiries <span className="text-gray-500 text-base">({inquiries.length})</span></div>
          <div className="space-y-3">
            {inquiries.map((q) => (
              <div key={q.id} className="border border-white/10 p-3" data-testid={`inquiry-row-${q.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-white font-medium">{q.company_name}</div>
                  {q.interested_tier && <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border border-white/20 text-gray-400">{q.interested_tier}</span>}
                </div>
                <div className="text-sm text-gray-400 mt-1">{q.contact_name} · <a href={`mailto:${q.email}`} className="text-brand hover:underline">{q.email}</a>{q.phone ? ` · ${q.phone}` : ""}</div>
                {q.message && <div className="text-sm text-gray-300 mt-2 bg-white/[0.03] p-2 border border-white/5">{q.message}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const Toggle = ({ label, on, onClick, testid }) => (
  <button type="button" onClick={onClick} className="flex items-center gap-2.5" data-testid={testid}>
    <span className={`relative w-10 h-5 rounded-full transition-colors ${on ? "bg-brand" : "bg-white/15"}`}>
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
    </span>
    <span className="text-sm text-gray-300">{label}</span>
  </button>
);
